<template>
  <Container id="alva-launcher-wrapper" class="flex justify-end is-wide">
    <!--    <div class="size-[75px] bg-blue-400 fixed bottom-20">alva</div>-->

    <div
      class="fixed rounded-[50px_50px_50px_50px] overflow-hidden size-[75px] bottom-20 right-20 md:right-auto motion-safe:transition-all motion-safe:ease-in-out motion-safe:duration-[350ms] z-[301]"
      :class="{
        'h-[95px] w-[294px] cursor-pointer !rounded-[50px_50px_50px_50px] !bottom-10':
          state === AlvaLauncherState.OPEN_HELLO,
        '!size-full !rounded-none md:!h-[429px] md:!w-[492px] md:!rounded-[20px_20px_38px_20px] !bottom-0 md:!bottom-20 !right-0 md:!right-auto':
          state === AlvaLauncherState.OPEN_FULL,
      }"
      role="button"
      tabindex="0"
      :aria-label="$texts('alvaLauncher.openLauncher', 'Alva Launcher öffnen')"
      @click="openFull"
      @keydown.enter="openFull"
    >
      <div
        class="bg-alva-hello size-full overflow-hidden relative"
        :class="{
          'animate-[alvaLauncherOpenFull_350ms_linear_0s_forwards]':
            state === AlvaLauncherState.OPEN_FULL,
        }"
      >
        <p
          v-if="state === AlvaLauncherState.OPEN_HELLO"
          class="absolute pl-40 pr-[85px] h-full flex items-center text-blue-900 text-base w-[294px] opacity-0 motion-safe:transition-all motion-safe:ease-in-out motion-safe:duration-[350ms]"
          :class="{
            'opacity-100': state === AlvaLauncherState.OPEN_HELLO,
          }"
          v-html="
            $texts(
              'alva.hello',
              'Ich bin Alva, ein<br />experimenteller<br />digitaler Assistent',
            )
          "
        />
        <div
          v-if="data && state === AlvaLauncherState.OPEN_FULL"
          class="size-full relative motion-safe:transition-all motion-safe:ease-in-out motion-safe:duration-[350ms] w-screen md:w-[492px] opacity-0"
          :class="{
            'opacity-100': state === AlvaLauncherState.OPEN_FULL,
          }"
        >
          <div
            class="z-10 absolute top-0 right-0 size-70 md:size-60 p-15 md:p-10 rounded-bl-[40px] bg-white/95 md:bg-transparent"
          >
            <button
              class="button is-strong is-icon-only"
              @click="toggleManually"
            >
              <SpriteSymbol name="close" class="size-20" aria-hidden="true" />
              <span class="sr-only">
                {{
                  $texts(
                    'alvaLauncher.closeLauncher',
                    'Alva Launcher schliessen',
                  )
                }}
              </span>
            </button>
          </div>
          <div
            class="md:absolute p-40 pt-100 md:pt-40 ck-content text-blue-900 !text-lg [&>p>a]:!text-blue-900 [&>p>a]:visited:!text-blue-900 [&>p>a]:!decoration-blue-900 [&>p>a]:visited:!decoration-blue-900 z-0"
            v-html="data.text"
          />
          <div
            class="md:absolute md:bottom-10 md:left-40 md:h-[76px] flex items-center ml-40 md:ml-0"
          >
            <AlvaButton consent-box-style="unstyled" @click="acceptAlva" />
          </div>
        </div>
      </div>
    </div>

    <button
      class="fixed bottom-20 right-20 md:!right-auto z-[2001] size-[75px]"
      @click="toggleManually"
    >
      <AlvaLogo />
      <!--      <div class="size-[75px] bg-yellow-700 rounded-full z-[250] opacity-45">alva</div>-->
    </button>
  </Container>
</template>

<script setup lang="ts">
const { isAccepted, accept, pauseHello, shouldShowHello } = useAlvaCookie()

const { data } = await useAsyncGraphqlQuery('alvaLauncherData', null, {
  transform: (v) => {
    return {
      url: v.data.globalConfig?.link?.uri?.path,
      text: v.data.globalConfig?.text,
    }
  },
  graphqlCaching: {
    client: true,
  },
})

enum AlvaLauncherState {
  CLOSED = 'closed',
  OPEN_HELLO = 'open_hello',
  OPEN_FULL = 'open_full',
}

const state = ref<AlvaLauncherState>(AlvaLauncherState.CLOSED)
const wasTriggeredManually = ref(false)

function toggleManually() {
  wasTriggeredManually.value = true

  if (state.value !== AlvaLauncherState.OPEN_FULL) {
    if (isAccepted.value) {
      openAlva()
      return
    }

    state.value = AlvaLauncherState.OPEN_FULL

    return
  }

  state.value = AlvaLauncherState.CLOSED
}

function toggleHello() {
  if (state.value === AlvaLauncherState.CLOSED) {
    state.value = AlvaLauncherState.OPEN_HELLO

    return
  }

  if (
    !wasTriggeredManually.value &&
    state.value === AlvaLauncherState.OPEN_HELLO
  ) {
    state.value = AlvaLauncherState.CLOSED
  }
}

function openFull() {
  if (state.value === AlvaLauncherState.OPEN_HELLO) {
    if (isAccepted.value) {
      openAlva()
      return
    }

    state.value = AlvaLauncherState.OPEN_FULL
  }
}

function openAlva() {
  if (data.value?.url) {
    window.open(data.value?.url, '_blank')?.focus()
  }
}

function acceptAlva() {
  accept()
  openAlva()
  state.value = AlvaLauncherState.CLOSED
}

const scrollTimeout = ref<NodeJS.Timeout | null>(null)
let hasScrolledOnce = false

const handleScroll = () => {
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value)
  }

  scrollTimeout.value = setTimeout(() => {
    if (!hasScrolledOnce) {
      hasScrolledOnce = true
    } else {
      // Remove scroll event listener after the second scroll.
      document.removeEventListener('scroll', handleScroll)
      // show the hello component.
      toggleHello()

      // Pause the hello component, so it doesn't show again immediately.
      pauseHello()

      // Set a timeout to show the hello component again after a while.
      setTimeout(() => {
        toggleHello()
      }, 5000)
    }
  }, 150)
}

onMounted(() => {
  if (shouldShowHello.value) {
    document.addEventListener('scroll', handleScroll)
  }
})

onBeforeUnmount(() => {
  // Clean up the scroll event listener and timeout.
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value)
  }

  // Remove the scroll event listener to prevent memory leaks.
  document.removeEventListener('scroll', handleScroll)
})
</script>
